import React, { useState, useContext } from 'react';
import Styled from 'styled';
import { FormattedMessage } from 'react-intl';
import { useMutation } from '@apollo/client/react/hooks';

import SVGIcon from 'lib/images/svg-icon/svg-icon';

import TextShortener from 'four-nets/ui-v2/text-shortener/text-shortener';
import FeedImages from 'four-nets/photoblog/posts/common/feed-images/feed-images';
import downArrow from 'four-nets/icons/svg/icon_arrow-down.svg';
import { ViewerContext } from 'four-nets/user/viewer/viewer';
import UpdateVendorAlbum from 'four-nets/vendor/update-vendor-album/update-vendor-album';
import TextArea from 'four-nets/ui-v2/form/text-area/text-area';
import IconClose from 'four-nets/icons/svg/icon_close.svg';
import Markdown from 'four-nets/ui-v2/markdown/markdown';

import { hideExpiredVendorContent } from '../permissions';

import MarkdownCheatSheet from './markdown-cheatsheet/markdown-cheat-sheet';
import VendorShowcase from './vendor-showcase/vendor-showcase';
import VendorVideos, { EditVendorVideos } from './vendor-videos/vendor-videos';

import VendorProducts from './vendor-products/vendor-products';
import mutation, {
    UpdateVendorAboutMutation,
    UpdateVendorAboutMutationVariables,
} from './mutations/update-vendor-about.graphql';
import { VendorPresentationFragment } from './vendor-presentation.graphql';
import defaultStyles from './vendor-presentation.scss';
import HtmlComponent from 'lib/html-component';

export interface Props extends VendorPresentationFragment {
    styles: typeof defaultStyles;
}

const MAX_ABOUT_LENGTH = 500;

const VendorPresentation: React.FC<Props> = props => {
    const { styles, ...vendor } = props;
    const { album, user, products } = vendor;

    const [showAll, setShowAll] = useState(false);
    const [openAbout, setOpenAbout] = useState(false);
    const [openAlbum, setOpenAlbum] = useState(false);
    const [about, setAbout] = useState(vendor.about || '');
    const [videos, setVideos] = useState(
        vendor.videoLinks && vendor.videoLinks.edges && vendor.videoLinks.edges.length
            ? vendor.videoLinks.edges.map(edge => edge!.node!)
            : []
    );
    const [videosEdit, setVideosEdit] = useState(false);

    const { viewer } = useContext(ViewerContext);

    const [updateVendorAbout] = useMutation<UpdateVendorAboutMutation, UpdateVendorAboutMutationVariables>(mutation);

    const isAuthor = Boolean(viewer && user && viewer.username === user.username);

    return hideExpiredVendorContent(vendor, isAuthor) ? null : (
        <>
            <UpdateVendorAlbum open={openAlbum} onClose={() => setOpenAlbum(false)} vendor={vendor as any} />
            <div className={styles.wrapper}>
                {about || isAuthor ? (
                    <div className={styles.infoWrapper}>
                        <FormattedMessage id='ABOUT_US_PAGE_TITLE'>
                            {msg => (
                                <div className={styles.headingWrapper}>
                                    <span className={styles.heading}>{msg}</span>
                                    {isAuthor ? (
                                        <button className={styles.editButton} onClick={() => setOpenAbout(!openAbout)}>
                                            <FormattedMessage id='EDIT' values={{ gender: 'v' }} />
                                        </button>
                                    ) : null}
                                </div>
                            )}
                        </FormattedMessage>
                        {isAuthor && openAbout ? (
                            <>
                                <TextArea
                                    value={about}
                                    onChange={e => {
                                        setAbout(e.target.value);
                                    }}
                                />
                                <button
                                    className={styles.button}
                                    onClick={() => {
                                        updateVendorAbout({ variables: { input: { about } } }).finally(() => {
                                            setOpenAbout(false);
                                            setShowAll(true);
                                        });
                                    }}
                                >
                                    <FormattedMessage id='SAVE' values={{ gender: 'v' }} />
                                </button>
                                <button
                                    type='button'
                                    onClick={() => {
                                        setOpenAbout(false);
                                        setAbout(vendor.about || '');
                                    }}
                                    className={styles.cancelButton}
                                >
                                    <SVGIcon
                                        style={{ image: styles.iconClose }}
                                        className={styles.iconClose}
                                        src={IconClose}
                                    />
                                    <FormattedMessage id='LEAVE_WITHOUT_SAVING' />
                                </button>
                                <MarkdownCheatSheet />
                            </>
                        ) : about ? (
                            <TextShortener text={about} textLength={showAll ? 0 : MAX_ABOUT_LENGTH}>
                                {msg => (
                                    <React.Fragment>
                                        <div className={styles.text}>
                                            <Markdown>{msg}</Markdown>
                                        </div>
                                        {showAll || !about || about.length < MAX_ABOUT_LENGTH ? null : (
                                            <button className={styles.showMoreButton} onClick={() => setShowAll(true)}>
                                                <FormattedMessage id='BUTTON_SHOW_MORE'>
                                                    {msg => <span>{msg.toString().toLowerCase()}</span>}
                                                </FormattedMessage>
                                                <SVGIcon style={{ image: styles.icon }} src={downArrow} />
                                            </button>
                                        )}
                                    </React.Fragment>
                                )}
                            </TextShortener>
                        ) : (
                            <FormattedMessage id='DESCRIBE_IN_MORE_DETAIL_WHAT_YOU_OFFER'>
                                {msg => <span className={styles.description}>{msg}</span>}
                            </FormattedMessage>
                        )}
                    </div>
                ) : null}
                {isAuthor || (album && album.images && album.images.edges && album.images.edges.length > 0) ? (
                    <div className={styles.infoWrapper}>
                        <FormattedMessage id='SHOWCASE_OF_OUR_WORK'>
                            {msg => (
                                <div className={styles.headingWrapper}>
                                    <span className={styles.heading}>{msg}</span>
                                    {isAuthor ? (
                                        <button className={styles.editButton} onClick={() => setOpenAlbum(true)}>
                                            <FormattedMessage id='EDIT' values={{ gender: 'v' }} />
                                        </button>
                                    ) : null}
                                </div>
                            )}
                        </FormattedMessage>
                        <FeedImages images={album.images} fetchMore={() => Promise.resolve()}>
                            {(imgRef: any, onClick: any) => (
                                <VendorShowcase
                                    imgRef={imgRef}
                                    onClick={onClick}
                                    images={album.images}
                                    imagesCount={album.imagesCount}
                                />
                            )}
                        </FeedImages>
                        {isAuthor && !(album && album.images && album.images.edges && album.images.edges.length > 0) ? (
                            <FormattedMessage id='ADD_SHOWCASE'>
                                {msg => <span className={styles.description}>{msg}</span>}
                            </FormattedMessage>
                        ) : null}
                    </div>
                ) : null}
                {isAuthor || videos.length > 0 ? (
                    <>
                        <FormattedMessage id='VENDOR_VIDEOS'>
                            {msg => (
                                <div className={styles.headingWrapper}>
                                    <h2 className={styles.heading}>{msg}</h2>
                                    {isAuthor ? (
                                        <button
                                            className={styles.editButton}
                                            onClick={() => setVideosEdit(!videosEdit)}
                                        >
                                            <FormattedMessage
                                                id={videosEdit ? 'BUTTON_LABEL_CLOSE' : 'EDIT'}
                                                values={{ gender: 'v' }}
                                            />
                                        </button>
                                    ) : null}
                                </div>
                            )}
                        </FormattedMessage>
                        {videosEdit ? (
                            <>
                                <FormattedMessage id='PRESENTATION_ADD_VIDEO_HINT'>
                                    {msg => <span className={styles.description}>{msg}</span>}
                                </FormattedMessage>
                                <EditVendorVideos
                                    links={videos.map(video => video.link)}
                                    onSubmit={videoLinks => {
                                        setVideos(videoLinks);
                                        setVideosEdit(false);
                                    }}
                                />
                            </>
                        ) : (
                            <VendorVideos links={videos} />
                        )}
                        {videos.length === 0 ? (
                            <FormattedMessage id='ADD_VIDEO_PLACEHOLDER'>
                                {msg => <span className={styles.description}>{msg}</span>}
                            </FormattedMessage>
                        ) : null}
                    </>
                ) : null}
                {isAuthor || products?.edges?.length ? (
                    <>
                        <div style={{ display: 'none' }}>
                            <VendorProducts
                                edges={products?.edges || []}
                                bazaarProductsCount={user.bazaarProductsCount}
                                username={user.username}
                            />
                        </div>
                        <HtmlComponent url={`/market/react/products/vendor/${user.username}/`} />
                    </>
                ) : null}
            </div>
        </>
    );
};

export default Styled(defaultStyles)(VendorPresentation);
